import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useCreatePropertyMutation } from '@/components/properties/services/propertyApi';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import {
  NewPropertyForm,
  buildPropertyRequest,
  type NewPropertySubmitValues,
} from '@/components/orders/new-order-drawer/components/NewPropertyForm';

interface NewPropertyDrawerProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Fired after a property is successfully created (drawer already closing). */
  onCreated?: () => void;
}

export function NewPropertyDrawer({ open, onOpenChange, onCreated }: NewPropertyDrawerProps) {
  const dispatch = useDispatch();
  const [createProperty] = useCreatePropertyMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  // Ref (not state): updating it from the form's dirty effect must not
  // re-render the drawer.
  const formDirtyRef = useRef(false);

  // Reset dirty tracking whenever the drawer closes so a stale "dirty"
  // can't gate a future close.
  useEffect(() => {
    if (!open) formDirtyRef.current = false;
  }, [open]);

  const triggerSubmit = () => {
    formRef.current?.requestSubmit();
  };

  const handleSheetOpenChange = (next: boolean) => {
    if (!next && formDirtyRef.current) {
      setShowDiscard(true);
      return;
    }
    onOpenChange(next);
  };

  const confirmDiscard = () => {
    formDirtyRef.current = false;
    setShowDiscard(false);
    onOpenChange(false);
  };

  const handleSubmit = async (values: NewPropertySubmitValues) => {
    setIsSubmitting(true);
    try {
      await createProperty(buildPropertyRequest(values)).unwrap();
      formDirtyRef.current = false;
      onOpenChange(false);
      dispatch(
        showAlert({ type: Variants.SUCCESS, message: 'Property created' })
      );
      onCreated?.();
    } catch (err) {
      // Keep the drawer open with the form contents intact so the user
      // can correct and retry. The slice extracts a message from the error.
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: err }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px] [&>button]:hidden"
          // Google Places renders its .pac-container on document.body,
          // outside this dialog; ignore outside pointer-downs originating
          // there so selecting a suggestion doesn't tear the drawer down.
          // (Paired with the global `.pac-container { pointer-events: auto }`
          // rule in app.css.)
          onPointerDownOutside={(e) => {
            const target = e.detail.originalEvent.target as Element | null;
            if (target?.closest('.pac-container')) e.preventDefault();
          }}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-8 py-6">
            <div className="flex-1">
              <SheetTitle className="text-2xl">Add a New Property</SheetTitle>
              <SheetDescription className="mt-1 text-sm text-muted-foreground">
                We'll save this to your property list.
              </SheetDescription>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => handleSheetOpenChange(false)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto px-8 py-8"
            ref={(el) => {
              // Capture the inner <form> so the footer Create button can
              // requestSubmit() while Formik keeps owning validation.
              formRef.current = el?.querySelector('form') ?? null;
            }}
          >
            <NewPropertyForm
              onSubmit={handleSubmit}
              onCancel={() => handleSheetOpenChange(false)}
              isSubmitting={isSubmitting}
              onDirtyChange={(dirty) => {
                formDirtyRef.current = dirty;
              }}
            />
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-card px-8 py-5">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleSheetOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={triggerSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Spinner className="h-4 w-4" /> : null}
              Create Property
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Modal
        open={showDiscard}
        onOpenChange={(next) => {
          if (!next) setShowDiscard(false);
        }}
        title="Discard Changes?"
        description="You have unsaved property details. Closing this form will discard them."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowDiscard(false)}>
              Keep Editing
            </Button>
            <Button variant="destructive" onClick={confirmDiscard}>
              Discard
            </Button>
          </>
        }
      />
    </>
  );
}
